import React, { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Container, Paper, Typography, Alert } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";

import AppButton from "../components/buttons/AppButton";
import AppCircularProgress from "../components/common/AppCircularProgress";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  verifyUserEmail,
  clearVerifyError,
} from "../redux/actions/authActions";

const EmailVerificationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const hasRequested = useRef(false);

  const { loadingUserVerify, errorUserVerify } = useAppSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    if (token && !hasRequested.current) {
      dispatch(verifyUserEmail(token));
      hasRequested.current = true;
    }
    return () => {
      dispatch(clearVerifyError());
    };
  }, [dispatch, token]);

  return (
    <Container maxWidth="sm" sx={{ py: 15, textAlign: "center" }}>
      <Paper sx={{ p: 5, borderRadius: 4, bgcolor: "background.paper" }}>
        {loadingUserVerify ? (
          <Box sx={{ py: 5 }}>
            <AppCircularProgress />
            <Typography variant="h5" sx={{ mt: 3 }}>
              Verifying your account...
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 1 }}>
              Please do not close this window.
            </Typography>
          </Box>
        ) : errorUserVerify ? (
          <Box sx={{ py: 3 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{ color: "error.main", mb: 2, fontWeight: 700 }}
            >
              Verification Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
              {errorUserVerify}
            </Alert>
            <AppButton variant="contained" onClick={() => navigate("/auth")}>
              Go to Login
            </AppButton>
          </Box>
        ) : (
          <Box sx={{ py: 3 }}>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: 80, mb: 2 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Verification Successful!
            </Typography>
            <Typography sx={{ color: "text.secondary", mb: 4 }}>
              Your email has been confirmed. You can now access all features of
              the portal.
            </Typography>
            <AppButton variant="gradient" onClick={() => navigate("/auth")}>
              Proceed to Login
            </AppButton>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default EmailVerificationPage;
